import {
  $applyNodeReplacement,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedTextNode,
  Spread,
  TextNode,
} from 'lexical';
import { FieldRefTarget } from '../../util/formula-vocabulary';

/** Plain field, a list (REDUCE's second argument), or a member of a list item. */
export type FieldChipVariant = 'field' | 'list' | 'member';

export type SerializedFieldChipNode = Spread<
  { fieldPath: string; label: string; variant?: FieldChipVariant },
  SerializedTextNode
>;

export const FIELD_CHIP_CLASS = 'formula-field-chip';

/**
 * Which chip a vocabulary entry gets — lists and their members are coloured
 * apart from plain fields. KPI period/scenario tokens borrow the member look:
 * they belong to their KPI the way members belong to their list.
 */
export function fieldChipVariant(
  target: Pick<FieldRefTarget, 'type' | 'list' | 'kpiToken'>
): FieldChipVariant {
  return target.type === 'list' ? 'list' : target.list || target.kpiToken ? 'member' : 'field';
}

/**
 * A chip's text form. Brackets are stripped from the label because the display
 * rendering is parsed for validation — a label containing "[" or "]" would
 * make an otherwise-valid formula unparseable. (A display path never carries
 * any; this guards labels from elsewhere.) The canonical rendering uses the
 * path and is unaffected.
 */
export function fieldChipText(label: string): string {
  return `[${label.replace(/[\][]/g, '')}]`;
}

/**
 * An atomic field reference in the formula editor: carries the canonical
 * path, renders "[Display.Path]" as a pill. 'token' mode (set on create) makes
 * it delete as one unit; canInsertTextBefore/After keep typed text from
 * merging into it.
 */
export class FieldChipNode extends TextNode {
  __fieldPath: string;
  __label: string;
  __variant: FieldChipVariant;

  static getType(): string {
    return 'formula-field-chip';
  }

  static clone(node: FieldChipNode): FieldChipNode {
    return new FieldChipNode(node.__fieldPath, node.__label, node.__variant, node.__key);
  }

  constructor(fieldPath: string, label: string, variant: FieldChipVariant = 'field', key?: NodeKey) {
    super(fieldChipText(label), key);
    this.__fieldPath = fieldPath;
    this.__label = label;
    this.__variant = variant;
  }

  getFieldPath(): string {
    return this.getLatest().__fieldPath;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.className =
      this.__variant === 'field'
        ? FIELD_CHIP_CLASS
        : `${FIELD_CHIP_CLASS} ${FIELD_CHIP_CLASS}--${this.__variant}`;
    return dom;
  }

  static importJSON(serializedNode: SerializedFieldChipNode): FieldChipNode {
    return $createFieldChipNode(
      serializedNode.fieldPath,
      serializedNode.label,
      serializedNode.variant ?? 'field'
    ).updateFromJSON(serializedNode);
  }

  exportJSON(): SerializedFieldChipNode {
    return {
      ...super.exportJSON(),
      type: FieldChipNode.getType(),
      fieldPath: this.__fieldPath,
      label: this.__label,
      variant: this.__variant,
    };
  }

  canInsertTextBefore(): boolean {
    return false;
  }

  canInsertTextAfter(): boolean {
    return false;
  }
}

export function $createFieldChipNode(
  fieldPath: string,
  label: string,
  variant: FieldChipVariant = 'field'
): FieldChipNode {
  return $applyNodeReplacement(new FieldChipNode(fieldPath, label, variant).setMode('token'));
}

export function $isFieldChipNode(node: LexicalNode | null | undefined): node is FieldChipNode {
  return node instanceof FieldChipNode;
}
